import { Archive, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { CategoryBadges } from './category-badges';
import { FirstShareMeta, SlackPermalink } from './first-share';
import { LinkFavicon, LinkThumbnail } from './link-media';
import { ProcessingStatusBadge } from '@/components/status-badge';
import { cn } from '@/lib/utils';
import type { LinkSummary } from '@/types/api';

/**
 * SECURITY, DELIBERATE: every string on this card (title, description, site name, Slack
 * display names) originates from an arbitrary third-party web page or from Slack. It is
 * rendered exclusively as JSX text nodes, which React escapes. `dangerouslySetInnerHTML` is
 * never used anywhere in this app — if you are tempted to "render the HTML description",
 * don't.
 */
export function LinkCard({
  link,
  categoryLabels,
}: {
  link: LinkSummary;
  categoryLabels?: Map<string, string>;
}) {
  const title = link.title?.trim() || link.domain;
  const showStatus = link.processingStatus !== 'COMPLETE';

  return (
    <article
      className={cn(
        'group flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card',
        'text-card-foreground shadow-sm transition-shadow focus-within:shadow-md hover:shadow-md',
      )}
    >
      <Link
        to={`/links/${encodeURIComponent(link.linkId)}`}
        tabIndex={-1}
        aria-hidden="true"
        className="block aspect-[16/9] w-full overflow-hidden border-b border-border bg-muted"
      >
        <LinkThumbnail link={link} />
      </Link>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            <LinkFavicon link={link} />
            <span className="truncate">{link.siteName?.trim() || link.domain}</span>
          </span>
          {link.archived ? (
            <Badge variant="outline" className="gap-1">
              <Archive aria-hidden="true" className="h-3 w-3" />
              Archived
            </Badge>
          ) : null}
          {showStatus ? <ProcessingStatusBadge status={link.processingStatus} /> : null}
        </div>

        <h3 className="text-sm font-semibold leading-snug">
          <Link
            to={`/links/${encodeURIComponent(link.linkId)}`}
            className={cn(
              'line-clamp-2 rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2',
              'focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
          >
            {title}
          </Link>
        </h3>

        {link.description?.trim() ? (
          <p className="line-clamp-3 text-sm text-muted-foreground">{link.description}</p>
        ) : null}

        <CategoryBadges slugs={link.categories} labels={categoryLabels} />

        <div className="mt-auto flex flex-col gap-3 pt-1">
          <FirstShareMeta share={link.firstShare} />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-3">
            <a
              href={link.originalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md text-xs font-medium text-primary',
                'hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              )}
            >
              <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
              Open original
              <span className="sr-only"> page for {title} (opens in a new tab)</span>
            </a>

            <SlackPermalink share={link.firstShare} label="First Slack message" />
          </div>
        </div>
      </div>
    </article>
  );
}
